'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { SearchApiServiceChannel } from '@/app/(main)/(pages)/_types/search-api-response.type';

export const SERVICE_CHANNELS: SearchApiServiceChannel[] = [
  SearchApiServiceChannel.IN_PERSON,
  SearchApiServiceChannel.TELEHEALTH,
];

export type EntityProceduresStatus = 'loading' | 'ready' | 'error';

export interface EntityProceduresPage<TGroup> {
  procedures: TGroup[];
  hasMore: boolean;
}

export interface EntityProceduresChannelState<TGroup> {
  procedures: TGroup[];
  hasMore: boolean;
  page: number;
  status: EntityProceduresStatus;
}

export type EntityProceduresByChannel<TGroup> = Record<
  SearchApiServiceChannel,
  EntityProceduresChannelState<TGroup>
>;

export interface UseEntityProceduresOptions<TGroup> {
  fetchPage: (params: {
    serviceChannel: SearchApiServiceChannel;
    page: number;
  }) => Promise<EntityProceduresPage<TGroup>>;
  mergeGroups: (current: TGroup[], incoming: TGroup[]) => TGroup[];
  enabled?: boolean;
}

export interface UseEntityProceduresReturn<TGroup> {
  activeChannel: SearchApiServiceChannel;
  availableChannels: SearchApiServiceChannel[];
  selectChannel: (channel: SearchApiServiceChannel) => void;
  channel: EntityProceduresChannelState<TGroup>;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  retry: () => void;
}

function makeChannelState<TGroup>(status: EntityProceduresStatus): EntityProceduresChannelState<TGroup> {
  return { procedures: [], hasMore: false, page: 1, status };
}

function makeState<TGroup>(status: EntityProceduresStatus): EntityProceduresByChannel<TGroup> {
  return {
    [SearchApiServiceChannel.IN_PERSON]: makeChannelState(status),
    [SearchApiServiceChannel.TELEHEALTH]: makeChannelState(status),
  };
}

function isChannelAvailable<TGroup>(channel: EntityProceduresChannelState<TGroup>): boolean {
  return channel.status === 'error' || channel.procedures.length > 0;
}

function resolveAvailableChannels<TGroup>(
  channels: EntityProceduresByChannel<TGroup>,
): SearchApiServiceChannel[] {
  return SERVICE_CHANNELS.filter((serviceChannel) => isChannelAvailable(channels[serviceChannel]));
}

export function useEntityProcedures<TGroup>({
  fetchPage,
  mergeGroups,
  enabled = true,
}: UseEntityProceduresOptions<TGroup>): UseEntityProceduresReturn<TGroup> {
  const [channels, setChannels] = useState<EntityProceduresByChannel<TGroup>>(
    () => makeState(enabled ? 'loading' : 'ready'),
  );
  const [activeChannel, setActiveChannel] = useState<SearchApiServiceChannel>(SearchApiServiceChannel.IN_PERSON);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setChannels(makeState('ready'));
      return;
    }

    let cancelled = false;

    setChannels(makeState('loading'));

    const loadChannel = async (
      serviceChannel: SearchApiServiceChannel,
    ): Promise<EntityProceduresChannelState<TGroup>> => {
      try {
        const { procedures, hasMore } = await fetchPage({ serviceChannel, page: 1 });

        return { procedures, hasMore, page: 1, status: 'ready' };
      } catch {
        return { procedures: [], hasMore: false, page: 1, status: 'error' };
      }
    };

    Promise.all(SERVICE_CHANNELS.map(loadChannel)).then(([inPerson, telehealth]) => {
      if (cancelled) {
        return;
      }

      const loaded: EntityProceduresByChannel<TGroup> = {
        [SearchApiServiceChannel.IN_PERSON]: inPerson,
        [SearchApiServiceChannel.TELEHEALTH]: telehealth,
      };
      const available = resolveAvailableChannels(loaded);

      setChannels(loaded);
      setActiveChannel((current) => (available.includes(current) ? current : available[0] ?? current));
    });

    return () => {
      cancelled = true;
    };
  }, [fetchPage, attempt, enabled]);

  const loadMore = useCallback(() => {
    const current = channels[activeChannel];

    if (!current.hasMore || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    fetchPage({ serviceChannel: activeChannel, page: current.page + 1 })
      .then(({ procedures, hasMore }) => {
        setChannels((previous) => ({
          ...previous,
          [activeChannel]: {
            procedures: mergeGroups(previous[activeChannel].procedures, procedures),
            hasMore,
            page: previous[activeChannel].page + 1,
            status: 'ready',
          },
        }));
      })
      .catch(() => undefined)
      .finally(() => setIsLoadingMore(false));
  }, [channels, activeChannel, isLoadingMore, fetchPage, mergeGroups]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  const availableChannels = useMemo(() => resolveAvailableChannels(channels), [channels]);
  const isLoading = SERVICE_CHANNELS.some((serviceChannel) => channels[serviceChannel].status === 'loading');

  return {
    activeChannel,
    availableChannels,
    selectChannel: setActiveChannel,
    channel: channels[activeChannel],
    isLoading,
    isLoadingMore,
    loadMore,
    retry,
  };
}
